import { isAxiosError } from 'axios'
import { ChangeEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { PriceAdjustmentsTab } from '../components/purchases/PriceAdjustmentsTab'
import { ProductEntriesTab } from '../components/purchases/ProductEntriesTab'
import { SuggestedOrdersTab } from '../components/purchases/SuggestedOrdersTab'
import { SuppliersTab } from '../components/purchases/SuppliersTab'
import { ManualEntryModal } from '../components/purchases/modals/ManualEntryModal'
import { PriceAdjustmentModal } from '../components/purchases/modals/PriceAdjustmentModal'
import { ReviewModal } from '../components/purchases/modals/ReviewModal'
import { UploadXmlModal } from '../components/purchases/modals/UploadXmlModal'
import { ManualEntryFormValues } from '../components/purchases/modals/manual-entry-form'
import { buildExcelPurchaseItemPayload, buildManualPurchaseItemPayload } from '../components/purchases/modals/manual-entry-payload'
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  ManualPurchasePayload,
  ExcelPurchasePayload,
  NewProductData,
  PriceAdjustment,
  PriceAdjustmentProduct,
  ProductFromXML,
  purchasesService,
  SuggestedProduct,
  Supplier,
} from '../services/purchases.service'
import { useSessionStore } from '../store/auth/auth.store'

export function Purchases() {
  const canAction = useSessionStore((state) => state.canAction)
  const canViewPurchases = canAction('purchases.purchase.view')
  const canViewSuppliers = canAction('purchases.supplier.view')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showManualEntryModal, setShowManualEntryModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [showPriceAdjustmentModal, setShowPriceAdjustmentModal] = useState(false)
  const [productEntriesRefreshKey, setProductEntriesRefreshKey] = useState(0)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [suggestedProducts, setSuggestedProducts] = useState<SuggestedProduct[]>([])
  const [priceAdjustments, setPriceAdjustments] = useState<PriceAdjustment[]>([])
  const [selectedAdjustment, setSelectedAdjustment] = useState<PriceAdjustment | null>(null)
  const [editableAdjustmentProducts, setEditableAdjustmentProducts] = useState<PriceAdjustmentProduct[]>([])
  const [keepPreviousPrice, setKeepPreviousPrice] = useState<Record<number, boolean>>({})
  const [adjustmentFilters, setAdjustmentFilters] = useState({ supplier: '', status: '', dateFrom: '', dateTo: '' })
  const [suggestedFilters, setSuggestedFilters] = useState({ dateFrom: '', dateTo: '' })
  const [xmlFile, setXmlFile] = useState<File | null>(null)
  const [uploadData, setUploadData] = useState({ paymentForm: '', paymentMethod: '', entryDate: '' })
  const [extractedProducts, setExtractedProducts] = useState<ProductFromXML[]>([])
  const [newProductsData, setNewProductsData] = useState<NewProductData[]>([])

  useEffect(() => {
    async function loadPrototypeData() {
      try {
        // Esta carga compartida se conserva hasta que los otros prototipos sean reemplazados.
        const [loadedSuppliers, loadedSuggested, loadedAdjustments] = await Promise.all([
          purchasesService.getSuppliers(),
          purchasesService.getSuggestedProducts(),
          purchasesService.getPriceAdjustments(),
        ])
        setSuppliers(loadedSuppliers)
        setSuggestedProducts(loadedSuggested)
        setPriceAdjustments(loadedAdjustments)
      } catch (error) {
        console.error('Error loading purchase prototypes:', error)
      }
    }
    void loadPrototypeData()
  }, [])

  const handleXmlUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file?.name.endsWith('.xml')) setXmlFile(file)
  }

  const handleProcessXml = async () => {
    const mockProducts = await purchasesService.processXmlMock()
    setExtractedProducts(mockProducts)
    setNewProductsData(mockProducts.filter((product) => product.id === 4 || product.id === 5).map((product) => ({
      id: product.id,
      name: product.name,
      satKey: product.satKey,
      categoryId: '',
      departmentId: '',
      productTypeId: '',
      requiresLot: false,
      expiryDate: '',
      unitId: '',
      description: '',
      isRegistered: false,
    })))
    setShowUploadModal(false)
    setShowReviewModal(true)
  }

  const confirmProductEntry = () => {
    setShowReviewModal(false)
    setXmlFile(null)
    setUploadData({ paymentForm: '', paymentMethod: '', entryDate: '' })
    setExtractedProducts([])
    setNewProductsData([])
  }

  const handleManualEntrySubmit = async (data: ManualEntryFormValues, excelFile?: File) => {
    const header = {
      reference_number: data.noteNumber.trim(),
      supplier_id: Number(data.supplierId),
      purchase_date: data.purchaseDate,
      entry_date: data.entryDate,
      payment_condition: data.paymentCondition,
      payment_method_id: Number(data.paymentMethodId),
    }
    try {
      if (excelFile) {
        const excelPayload: ExcelPurchasePayload = {
          ...header,
          items: data.products.map(buildExcelPurchaseItemPayload),
        }
        await purchasesService.createExcelPurchase(excelPayload, excelFile)
        toast.success('Ingreso desde Excel registrado exitosamente')
      } else {
        const manualPayload: ManualPurchasePayload = {
          ...header,
          items: data.products.map(buildManualPurchaseItemPayload),
        }
        await purchasesService.createManualPurchase(manualPayload)
        toast.success('Ingreso manual registrado exitosamente')
      }
      setShowManualEntryModal(false)
      setProductEntriesRefreshKey((value) => value + 1)
    } catch (error: unknown) {
      const message = isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined
      toast.error(message || 'No fue posible registrar el ingreso')
    }
  }

  const openPriceAdjustmentModal = (adjustment: PriceAdjustment) => {
    setSelectedAdjustment(adjustment)
    setEditableAdjustmentProducts([...adjustment.products])
    setKeepPreviousPrice({})
    setShowPriceAdjustmentModal(true)
  }

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Compras y Proveedores</h1>

      <Tabs defaultValue="suggested" className="space-y-6">
        <TabsList>
          <TabsTrigger value="suggested">Pedidos Sugeridos</TabsTrigger>
          {canViewPurchases && <TabsTrigger value="products">Productos Ingresados</TabsTrigger>}
          <TabsTrigger value="adjustments">Ajustes de Precio</TabsTrigger>
          {canViewSuppliers && <TabsTrigger value="suppliers">Proveedores</TabsTrigger>}
        </TabsList>

        <SuggestedOrdersTab suggestedProducts={suggestedProducts} suggestedFilters={suggestedFilters} setSuggestedFilters={setSuggestedFilters} />
        {canViewPurchases && (
          <ProductEntriesTab
            refreshKey={productEntriesRefreshKey}
            openXmlEntry={() => setShowUploadModal(true)}
            openManualEntry={() => setShowManualEntryModal(true)}
          />
        )}
        <PriceAdjustmentsTab
          priceAdjustments={priceAdjustments}
          suppliers={suppliers}
          adjustmentFilters={adjustmentFilters}
          setAdjustmentFilters={setAdjustmentFilters}
          openPriceAdjustmentModal={openPriceAdjustmentModal}
        />
        {canViewSuppliers && <SuppliersTab />}
      </Tabs>

      <UploadXmlModal
        showUploadModal={showUploadModal}
        setShowUploadModal={setShowUploadModal}
        xmlFile={xmlFile}
        setXmlFile={setXmlFile}
        handleXmlUpload={handleXmlUpload}
        uploadData={uploadData}
        setUploadData={setUploadData}
        handleProcessXml={handleProcessXml}
      />
      <ManualEntryModal
        showManualEntryModal={showManualEntryModal}
        setShowManualEntryModal={setShowManualEntryModal}
        onConfirm={handleManualEntrySubmit}
      />
      <ReviewModal
        showReviewModal={showReviewModal}
        setShowReviewModal={setShowReviewModal}
        extractedProducts={extractedProducts}
        newProductsData={newProductsData}
        setNewProductsData={setNewProductsData}
        confirmProductEntry={confirmProductEntry}
        setShowUploadModal={setShowUploadModal}
      />
      <PriceAdjustmentModal
        showPriceAdjustmentModal={showPriceAdjustmentModal}
        setShowPriceAdjustmentModal={setShowPriceAdjustmentModal}
        selectedAdjustment={selectedAdjustment}
        editableAdjustmentProducts={editableAdjustmentProducts}
        setEditableAdjustmentProducts={setEditableAdjustmentProducts}
        keepPreviousPrice={keepPreviousPrice}
        setKeepPreviousPrice={setKeepPreviousPrice}
      />
    </div>
  )
}
